import PropTypes from 'prop-types'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react'
import AceEditor from 'react-ace'
import 'ace-builds/src-noconflict/mode-jsx'
import 'ace-builds/src-noconflict/theme-xcode'
import 'ace-builds/src-noconflict/ext-searchbox'
import { abundanceComponents } from '../../../../../../abundanceEngine/abundanceComponentsRegister'
import { registerEditorCommands } from './utils/editorCommands'
import useEditorStyles from './utils/editorStyles'
import { formatJsxSource, validateJsxSource } from './utils/jsxUtils'

const ContentCodeView = forwardRef(({
  jsxContent,
  onJsxContentChange,
  onDiagnosticsChange,
  onComponentClick
}, ref) => {
  const classes = useEditorStyles()
  const editorRef = useRef(null)
  const currentValueRef = useRef(jsxContent || '')
  const validationTimeoutRef = useRef(null)
  const onComponentClickRef = useRef(onComponentClick)
  onComponentClickRef.current = onComponentClick

  const updateDiagnostics = useCallback((diagnostics) => {
    const editor = editorRef.current?.editor
    if (editor) {
      editor.getSession().setAnnotations(diagnostics)
    }
    if (onDiagnosticsChange) {
      onDiagnosticsChange(diagnostics)
    }
  }, [onDiagnosticsChange])

  const validateJsx = useCallback((value) => {
    updateDiagnostics(validateJsxSource(value))
  }, [updateDiagnostics])

  const scheduleValidation = useCallback((value) => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current)
    }
    validationTimeoutRef.current = setTimeout(() => {
      validateJsx(value)
    }, 250)
  }, [validateJsx])

  const formatEditorContent = useCallback(async () => {
    const editor = editorRef.current?.editor
    const value = currentValueRef.current || ''
    if (!editor || !value.trim()) return

    try {
      const formattedValue = await formatJsxSource(value)
      if (formattedValue === value) return

      const cursorPosition = editor.getCursorPosition()
      editor.setValue(formattedValue, -1)
      editor.clearSelection()
      currentValueRef.current = formattedValue

      const maxRow = Math.max(editor.session.getLength() - 1, 0)
      const row = Math.min(cursorPosition.row, maxRow)
      const line = editor.session.getLine(row) || ''
      editor.moveCursorTo(row, Math.min(cursorPosition.column, line.length))

      if (onJsxContentChange) {
        onJsxContentChange(formattedValue)
      }
      scheduleValidation(formattedValue)
    } catch (error) {
      console.error('Failed to format JSX content', error)
    }
  }, [onJsxContentChange, scheduleValidation])

  // Expose getValue method to parent components
  useImperativeHandle(ref, () => ({
    getValue: () => currentValueRef.current,
    focus: () => {
      if (editorRef.current?.editor) {
        editorRef.current.editor.focus()
      }
    },
    find: () => {
      if (editorRef.current?.editor) {
        editorRef.current.editor.execCommand('find')
      }
    },
    replace: () => {
      if (editorRef.current?.editor) {
        editorRef.current.editor.execCommand('replace')
      }
    },
    findNext: () => {
      if (editorRef.current?.editor) {
        editorRef.current.editor.findNext()
      }
    },
    findPrevious: () => {
      if (editorRef.current?.editor) {
        editorRef.current.editor.findPrevious()
      }
    },
    undo: () => {
      if (editorRef.current?.editor) {
        editorRef.current.editor.undo()
      }
    },
    redo: () => {
      if (editorRef.current?.editor) {
        editorRef.current.editor.redo()
      }
    },
    format: formatEditorContent
  }), [formatEditorContent])

  // Only update editor when prop changes externally (e.g., AI generation, revert)
  useEffect(() => {
    if (jsxContent !== currentValueRef.current) {
      currentValueRef.current = jsxContent || ''
      // Editor will update via defaultValue on mount, or we can force update if needed
      if (editorRef.current?.editor) {
        const editor = editorRef.current.editor
        const cursorPosition = editor.getCursorPosition()
        editor.setValue(jsxContent || '', 1)
        editor.moveCursorToPosition(cursorPosition)
      }
    }
    scheduleValidation(jsxContent || '')
  }, [jsxContent, scheduleValidation])

  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current)
      }
    }
  }, [])

  const handleChange = (value) => {
    currentValueRef.current = value
    scheduleValidation(value)
    if (onJsxContentChange) {
      onJsxContentChange(value)
    }
  }

  const handleEditorLoad = useCallback((editor) => {
    registerEditorCommands(editor, {
      onFormat: formatEditorContent
    })
    validateJsx(currentValueRef.current)

    editor.on('dblclick', () => {
      if (!onComponentClickRef.current) return
      const pos = editor.getCursorPosition()
      const token = editor.session.getTokenAt(pos.row, pos.column)
      if (!token) return
      const word = token.value.trim()
      if (word && abundanceComponents[word]) {
        onComponentClickRef.current(word)
      }
    })
  }, [formatEditorContent, validateJsx])

  return (
    <div className={`h-full ${classes.editorRoot}`}>
      <AceEditor
        ref={editorRef}
        height='calc(100%)'
        width='100%'
        mode='jsx'
        theme='xcode'
        name='jsx-editor'
        editorProps={{ $blockScrolling: true }}
        defaultValue={jsxContent || ''}
        onLoad={handleEditorLoad}
        onChange={handleChange}
        showPrintMargin={false}
        showGutter
        highlightActiveLine
        tabSize={2}
        setOptions={{
          enableMultiselect: true,
          useWorker: false
        }}
      />
    </div>
  )
})

ContentCodeView.displayName = 'ContentCodeView'

ContentCodeView.propTypes = {
  jsxContent: PropTypes.string,
  onJsxContentChange: PropTypes.func,
  onDiagnosticsChange: PropTypes.func,
  onComponentClick: PropTypes.func
}

export default ContentCodeView
